import {
  Alert,
  Card,
  Flex,
  Segmented,
  Select,
  Space,
  Typography,
  Upload,
} from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import type { ParsedJsonUpload, PipelineRunMode } from '../../types';
import { EvimoTable } from '~/app/components/common/EvimoTable/EvimoTable';

const { Text } = Typography;
const { Dragger } = Upload;

type PipelineSetupCardProps = {
  organizationId: string;
  assetId: string;
  isSuperAdmin: boolean;
  organizations: Array<{ id: string; name: string }>;
  onOrganizationIdChange: (organizationId: string) => void;
  assets: Array<{ id: string; name: string }>;
  onAssetIdChange: (assetId: string) => void;
  isLoadingAssets: boolean;
  runMode: PipelineRunMode;
  onRunModeChange: (mode: PipelineRunMode) => void;
  uploads: ParsedJsonUpload[];
  columns: ColumnsType<ParsedJsonUpload>;
  onFilesSelected: (files: File[]) => void;
};

export function PipelineSetupCard({
  organizationId,
  assetId,
  isSuperAdmin,
  organizations,
  onOrganizationIdChange,
  assets,
  onAssetIdChange,
  isLoadingAssets,
  runMode,
  onRunModeChange,
  uploads,
  columns,
  onFilesSelected,
}: PipelineSetupCardProps) {
  const { t } = useTranslation('pipelineDebug');
  const canSelectAsset = Boolean(organizationId);

  return (
    <Card
      title={t('setup.title')}
      className="pipeline-debug-card"
      styles={{ body: { padding: 16 } }}
    >
      <Flex vertical gap={14}>
        <Space direction="vertical" style={{ width: '100%' }} size={6}>
          <Text strong>{t('organizationLabel')}</Text>
          {isSuperAdmin ? (
            <Select
              placeholder={t('organizationPlaceholder')}
              value={organizationId || undefined}
              onChange={onOrganizationIdChange}
              options={organizations.map((organization) => ({
                label: organization.name,
                value: organization.id,
              }))}
              showSearch
              style={{ width: '100%' }}
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toString()
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          ) : (
            <Alert type="info" showIcon message={t('organizationLocked')} />
          )}
        </Space>

        <Space direction="vertical" style={{ width: '100%' }} size={6}>
          <Text strong>{t('assetLabel')}</Text>
          <Select
            placeholder={t('assetPlaceholder')}
            value={assetId || undefined}
            onChange={onAssetIdChange}
            options={assets.map((asset) => ({
              label: asset.name,
              value: asset.id,
            }))}
            showSearch
            style={{ width: '100%' }}
            disabled={!canSelectAsset}
            loading={isLoadingAssets}
            filterOption={(input, option) =>
              (option?.label ?? '')
                .toString()
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          />
        </Space>

        <Space direction="vertical" style={{ width: '100%' }} size={6}>
          <Text strong>{t('runModeLabel')}</Text>
          <Segmented
            block
            value={runMode}
            onChange={(value) => onRunModeChange(value as PipelineRunMode)}
            options={[
              { label: t('runModes.stepByStep'), value: 'step-by-step' },
              { label: t('runModes.continuous'), value: 'continuous' },
            ]}
          />
        </Space>

        <Dragger
          multiple
          accept=".json,application/json"
          className="pipeline-debug-upload"
          beforeUpload={(file) => {
            onFilesSelected([file]);
            return false;
          }}
          showUploadList={false}
          disabled={!organizationId || !assetId}
        >
          <Flex vertical align="center" gap={6} style={{ padding: '4px 0' }}>
            <InboxOutlined style={{ fontSize: 32, color: '#1E6DF6' }} />
            <Text strong style={{ fontSize: 14 }}>
              {t('upload.title')}
            </Text>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {t('upload.hint')}
            </Text>
          </Flex>
        </Dragger>

        <div className="pipeline-debug-file-table">
          <EvimoTable
            className="app-table"
            columns={columns}
            dataSource={uploads}
            rowKey="id"
            pagination={false}
            locale={{ emptyText: t('upload.empty') }}
            size="small"
          />
        </div>
      </Flex>
    </Card>
  );
}
